/**
 * The one hook both live-thread surfaces — `ThreadPane`'s transcript and
 * `ContextPane`'s ticket/identity/thread body — use to keep their
 * `ops.thread()`-fetched `ThreadDetail` from going stale. See
 * task-42-report.md for the two bugs this one hook closes, which turned out
 * to share a single root cause:
 *
 *   - `ContextPane` fetched `ThreadDetail` in an effect keyed only on
 *     `activeId`. Linking a ticket (or closing, hand-off, …) does not change
 *     `activeId`, so the fetch never re-ran, and the modal that performed
 *     the mutation updates the STORE (`upsertThread`), not this component's
 *     local state — the pane sat on the stale "unlinked" view with no Force
 *     sync button, the exact bug the owner hit.
 *   - `ThreadPane` had the identical `[activeId]`-only effect for its own
 *     `ThreadDetail`, and the SSE `thread` event that DOES arrive when the
 *     other party sends a message only ever carries a `ThreadSummary` — no
 *     `msgs` — so the open transcript never learned a reply had arrived.
 *     `activeId` unchanged, `detail` unchanged, message invisible until a
 *     hard refresh.
 *
 * Every `ThreadSummary` field the store carries (ticket, state, owner,
 * heldWrites, handOffState, lastActivity, …) is already kept live for every
 * thread by two things: `upsertThread` (every operation's own response —
 * TicketModal, CloseModal, HandoffModal, `send`, `claim`, … — is pushed
 * through it) and the SSE `thread` event (`subscribeStream`, itself just an
 * `upsertThread` call). Neither surface needs to fetch anything to learn one
 * of those changed — this hook reads them straight from the store on every
 * render (see `./mergeDetail.ts`, which is what actually joins the two
 * sources). The five fields `ThreadSummary` does NOT carry (`msgs`,
 * `closeNote`, `lastSyncedAt`, `handOffDisclosure`, `siblings`) still have to
 * come from `ops.thread()`, and THAT fetch now re-runs whenever a store
 * update implies one of them may be stale — not only when `activeId`
 * changes:
 *
 *   - `ticket` changing (linking/changing a ticket moves the sibling set)
 *   - `state` changing (closing sets `closeNote`)
 *   - `heldWrites` changing (a force-sync or any other write settles the
 *     pending count)
 *   - `handOffState` changing (a hand-off sets `handOffDisclosure`)
 *   - `lastActivity` changing — the signal that a message may have arrived;
 *     `send` and every inbound webhook touch it, and it is the only
 *     `ThreadSummary` field that moves on a plain message with no other
 *     state change (services/ops/src/routes/inbound.ts also publishes a
 *     full `ThreadDetail` on an inbound message, but the console's own
 *     `upsertThread` only ever reads `ThreadSummary`-shaped fields off it —
 *     `lastActivity` is what actually reaches this hook).
 *
 * Nothing here polls. The store only changes when the stream or an
 * operation's own response says something did — this hook just stops
 * ignoring that once a thread is already selected.
 */
import { useEffect, useState } from 'react'
import type { ThreadDetail, ThreadSummary } from '@communicator/shared'
import { ops } from './ops'
import { useStore } from '../store'
import { mergeThreadDetail } from '../components/context/mergeDetail'

export interface UseThreadDetailResult {
  /** `ThreadSummary` fields from the live store, `ThreadDetail`-only fields from the last `ops.thread()` read — see `./mergeDetail.ts`. `null` until the first read for this thread resolves. */
  detail: ThreadDetail | null
  /** The store's own live `ThreadSummary` for the active thread — `null` when nothing is selected, or (in unit tests that set `activeId` without ever populating `threads`) when the store simply has no entry for it yet. */
  storeSummary: ThreadSummary | null
  /** Re-runs the `ops.thread()` fetch right now — the escape hatch a caller's own mutation (force-sync, send, claim, …) uses to see its own result immediately rather than wait on the store to imply staleness. */
  refetch: () => Promise<void>
}

export function useThreadDetail(activeId: string | null): UseThreadDetailResult {
  const threads = useStore((s) => s.threads)
  const storeSummary = activeId ? (threads.find((t) => t.id === activeId) ?? null) : null

  const [detail, setDetail] = useState<ThreadDetail | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!activeId) {
      setDetail(null)
      return
    }
    ops
      .thread(activeId)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch(() => {
        if (!cancelled) setDetail(null)
      })
    return () => {
      cancelled = true
    }
    // The dependency list is the whole point of this hook — see its own
    // header comment for why each of these, and only these, belongs here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeId,
    storeSummary?.ticket,
    storeSummary?.state,
    storeSummary?.heldWrites,
    storeSummary?.handOffState,
    storeSummary?.lastActivity,
  ])

  async function refetch(): Promise<void> {
    if (!activeId) return
    try {
      const fresh = await ops.thread(activeId)
      setDetail(fresh)
    } catch {
      // Same honest-empty reasoning as the effect above — a failed refetch
      // leaves whatever was last successfully fetched in place.
    }
  }

  return { detail: mergeThreadDetail(detail, storeSummary), storeSummary, refetch }
}
